import { useEffect, useState } from 'react';

import { getInspectionsProfit, getSireReviewProfit, InspectionsProfit, SireReviewProfit } from '@/services/dashboardService';
import { LoadingSpinner } from '@/components/Dashboard/LoadingSpinner';
import { getCurrentMonth } from '@/utils/date';

import './dashboard.css';
import { DataStatus } from './dataStatus';
import chartColors from './chartColors';
import { formatCurrency } from '@/utils/currency';
import { CURRENCY, SERVICES } from '@/common/constants';
import MonthSelector from './MonthSelector';

type IncomeExpenseDisplayProps = {
    type: string,
}

type IncomeData = {
    income: number,
    expenses: number,
    profit: number,
}

const emptyData: IncomeData = {
    income: 0,
    expenses: 0,
    profit: 0,
}

const titles: {[serviceType: string]: string} = {
    'inspection': 'Inspection Revenue',
    'sire-review': 'SIRE Review Revenue',
}

const {darkred, darkgreen, darkblue, red} = chartColors;

const getProfitColor = (profit: number) => {
    if (profit < 0){
        return red;
    }
    if (profit > 0){
        return darkgreen;
    }
    return '#000000';
};

const IncomeExpenseDisplay = ({ type }: IncomeExpenseDisplayProps) => {
    const {year: currentYear, month: currentMonth} = getCurrentMonth();

    const [data, setData] = useState<IncomeData>(emptyData);
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const transformDataInspection = (originalData: InspectionsProfit): IncomeData => ({
        income: originalData.operatorInvoicesCompleted,
        expenses: originalData.inspectionCompanyInvoicesCompleted,
        profit: originalData.profitCompleted,
    });

    const transformDataSireReview = (originalData: SireReviewProfit): IncomeData => ({
        income: originalData.requestorInvoicesCompleted,
        expenses: originalData.sireReviewInvoicesCompleted,
        profit: originalData.profitCompleted,
    });

    const getDataInspection = async (month: number, year: number) => {
        const response = await getInspectionsProfit(month, year);
        if (!response.data){
            setDataStatus(DataStatus.ERROR);
            setData(emptyData);
            return;
        }
        if (response.data[0] == null || response.data.length === 0){
            setDataStatus(DataStatus.EMPTY);
            setData(emptyData);
            return;
        }
        const originalData = response.data[0] as InspectionsProfit;
        setData(transformDataInspection(originalData))
        setDataStatus(DataStatus.OK);
    };

    const getDataSireReview = async (month: number, year: number) => {
        const response = await getSireReviewProfit(month, year);
        if (!response.data){
            setDataStatus(DataStatus.ERROR);
            setData(emptyData);
            return;
        }
        if (response.data[0] == null || response.data.length === 0){
            setDataStatus(DataStatus.EMPTY);
            setData(emptyData);
            return;
        }
        const originalData = response.data[0] as SireReviewProfit;
        setData(transformDataSireReview(originalData))
        setDataStatus(DataStatus.OK);
    };

    const getData = async (month: number, year: number) => {
        setDataStatus(DataStatus.LOADING)
        if (type === SERVICES.INSPECTION){
            await getDataInspection(month, year)
        } else if (type === SERVICES.SIRE_REVIEW){
            await getDataSireReview(month, year)
        }
    }

    useEffect(() => {
        getData(currentMonth, currentYear);
    }, []);

    useEffect(() => {
        getData(month, year);
    }, [month, year]);

    return (
        <div className='card-full-small'>
            <h2 className='card-title'>{titles[type]}</h2>
            <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
            {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
            {/*{(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}*/}
            {/*(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) &&*/ <div className='income-container'>
                <div className='income-card'>
                    <div className='income-card-header' style={{ backgroundColor: darkgreen, color: '#ffffff' }}>
                        Total Revenue
                    </div>
                    <div className='income-card-content'>
                        {formatCurrency(data.income, CURRENCY.IDR.code, 'id-ID')}
                    </div>
                </div>
                <div className='income-card'>
                    <div className='income-card-header' style={{ backgroundColor: darkred, color: '#ffffff' }}>
                        Total Expenses
                    </div>
                    <div className='income-card-content'>
                        {formatCurrency(data.expenses, CURRENCY.IDR.code, 'id-ID')}
                    </div>
                </div>
                <div className='income-card'>
                    <div className='income-card-header' style={{ backgroundColor: darkblue, color: '#ffffff' }}>
                        Profit
                    </div>
                    <div className='income-card-content' style={{ color: getProfitColor(data.profit) }}>
                        {formatCurrency(data.profit, CURRENCY.IDR.code, 'id-ID')}
                    </div>
                </div>
            </div>}
        </div>
    );
};

export default IncomeExpenseDisplay;
